import {
    Button,
    Space,
    Image,
    Switch,
    Tooltip,
    Checkbox,
    Popover,
    Table,
    Input,
    Select,
    Card,
    List,
    TablePaginationConfig,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState, useRef, useMemo } from 'react';
import fetchApi from './fetchApi';
import antdMessage from './antdMessage';
import { wipeData, PageStoreProps, submitData } from '../../reduxSaga/reducer/showPageReducer';
import { useDispatch, useSelector } from 'react-redux';
import { CommonProps, createDeclareType, createTypeFactory, defaultRtn, RtnType, ShowPageProps, universalBasicTypeFactory } from '../../../utilsTs/declareTypes';
import BaseWebUniversalLogger, { BaseWebUniversalLoggerProps } from './logger/BaseWebUniversalLogger';
import getList from '../../universal/getList';
import FilesExtendsManage, { FilesExtendsManageProps } from '../files/FilesExtendsManage';
import { BaseFilesProps } from '../files/BaseFiles';
import stringHandle from '../../../utilsTs/baseLib/stringHandle';
import { TableRowSelection } from 'antd/es/table/interface';
import BaseWebEditUniversal, { BaseWebEditUniversalProps, EditMode } from './BaseWebEditUniversal';

/** 删除按钮选项属性 */
interface DeleteButtonOptions {
    /** 是否使用复选框 */
    useCheckBox?: boolean;
    /** 复选框内容 */
    checkBoxContent?: string;
    /** 复选框是否被选中(默认不选中) */
    isChecked?: boolean;
};

/** 视图模式对象 */
const ViewModes = createTypeFactory({
    /** 表格 */
    table: { id: 'table', name: '表格', isSelect: true },
    /** 图形 */
    gallery: { id: 'gallery', name: '图形', isSelect: true },
    /** 全部 */
    both: { id: 'both', name: '全部', isSelect: false }
} as const);

/** 视图模式string类型 */
type ViewModeStringType = ReturnType<typeof ViewModes.getIds>[number];

/** 日志属性 */
export interface LoggerProps<
    F extends universalBasicTypeFactory<any> = universalBasicTypeFactory<any>
> extends Pick<
    BaseWebUniversalLoggerProps<F>,
    'LoggerTypes' | 'loggerTypeId' | 'loggerServiceUrl' | 'queryType'
> {
    /** 日志显示 */
    showLogger?: boolean;
    /** 单个数据日志显示 */
    showSingleLogger?: boolean;
};

/**
 * 通用布尔开关列配置
 * 用于 showStates、showDefault 等以 Switch 展示布尔字段的列，
 * 允许自定义列标题、字段名、开关文案以及真假值。
 */
export interface ShowBooleanColumnOptions {
    /** 列标题 */
    title?: string;
    /** 字段名 */
    columnName?: string;
    /** 打开状态显示文字 */
    checkedChildren?: string;
    /** 关闭状态显示文字 */
    unCheckedChildren?: string;
    /** 关闭时的值（有些后端使用 0/1） */
    falseValue?: boolean | number;
    /** 打开时的值（有些后端使用 0/1） */
    trueValue?: boolean | number;
}

/** 显示选项属性 */
export interface DisplayOptionsProps extends Pick<ShowPageProps, 'pageSize'> {
    /** 是否自动获取数据，默认 true */
    autoFetch?: boolean;
    /** 显示模式：'table' 仅表格，'gallery' 仅图形，'both' 表格和图形可切换，默认 'table' */
    viewMode?: ViewModeStringType;
    /** 图形模式使用的列定义（当 displayMode 为 'gallery' 或 'both' 时生效） */
    galleryColumns?: Array<any>;
    /** 图形模式每页数 */
    gallerPageSize?: number;
    /** 显示新增按钮 */
    showAdd?: boolean;
    /** 显示查询 */
    showQuery?: boolean;
    /** 显示图片 */
    showImage?: boolean;
    /** 显示下载 */
    showDownload?: boolean;
    /** 显示默认（布尔值或自定义配置） */
    showDefault?: boolean | ShowBooleanColumnOptions;
    /** 显示状态（布尔值或自定义配置） */
    showStates?: boolean | ShowBooleanColumnOptions;
    /** 显示操作区域 */
    showOperation?: boolean;
    /** 显示删除按钮 */
    showDelete?: boolean;
    /** 删除按钮选项 */
    deleteButton?: DeleteButtonOptions;
    /** 显示编辑按钮 */
    showEdit?: boolean;
    /** 显示文件按钮 */
    showFiles?: boolean;
    /** 显示备份 */
    showUseBak?: boolean;
    /** 是否启用在表格中选择行（显示复选框） */
    showRowSelection?: boolean;
    /** 日志属性 */
    loggerProps?: LoggerProps;
}

/** 禁止选项属性 */
export interface DisabledOptionsProps {
    /** 禁用增加按钮 */
    disabledAdd?: boolean;
};

/** 删除选项 */
export interface DeleteOptions extends Pick<ShowPageProps, 'sendPageData'> {
    /** 记录集合 */
    record: any;
};

/** 展开列选项 */
interface ExpandableOptions {
    /** 子业务列 */
    subColumns?: Array<any>;
    /** 子表格数据数组对象名称，当为undefined时，如表格内容较多，可分两行展示 */
    subDataName?: string;
    /** 包含主数据内容 */
    isIncludingMasterData?: boolean;
    /** 是否使用主表格的显示选项 */
    isSameMainDisplayOptions?: boolean;
    /** 显示选项属性 */
    displayOptions?: Pick<DisplayOptionsProps,
        'showStates' | 'showEdit' | 'showDelete' | 'deleteButton' |
        'showDefault' | 'showDownload' | 'showOperation' | 'showFiles' | 'showImage' | 'loggerProps'>;
    /** 子表格是否按树形结构展示 */
    isTree?: boolean;
    /** 如果子数据是扁平的，指定父级关联字段，默认 'parentId' */
    parentKey?: string;
    /** 子表关健字，默认：'children' */
    childrenKey?: string;
    /**
     * 是否使用系统自带的树形展开（即依赖数据中的 children 字段，由 antd 自动构建树形结构）。
     *
     * - `true`  ：系统树形模式。直接使用数据中的 `children` 字段，**不传** `expandedRowRender`。
     * - `false` ：自定义模式（默认）。走 `expandedRowRender` 自定义渲染，
     *             内部会通过 `childrenColumnName` 屏蔽掉 `children` 字段，避免 antd 自动识别为树形数据。
     *
     * 两种模式互斥，由本开关控制。
     */
    useSystemTree?: boolean;
};

/** 文件选项 */
interface FilesOptions extends Pick<BaseFilesProps, 'insertFile' | 'defaultSelectedOptions'> {
    /** 文件业务标识，默认：apiId + 'File' */
    filesApiId?: string;
    /** 文件父级对象id */
    fileParentId?: string;
    /** 文件业务列 */
    filesColumns: Array<any>;
    /** 编辑组件 */
    FilesEditComponent?: any;
    /** 文件服务地址 */
    filesServiceUrl?: string;
    /** 图片数据数组名称 */
    filesArrayName?: string;
};

/** 数据执行类型 */
export type DataActionType = 'paginate' | 'query' | 'viewModeChange' | 'useBakChange' | 'sort' | 'filter';

/** BaseWebUniversal属性 */
export interface BaseWebUniversalProps extends
    Pick<CommonProps, 'apiId' | 'columns' | 'defaultService' | 'titleName' | 'primaryKeyName'>,
    Pick<ShowPageProps, 'sendPageData'> {
    /** 行选择配置，传入后内部会合并基础状态 */
    rowSelectionConfig?: Omit<TableRowSelection<any>, 'onChange' | 'selectedRowKeys'> & {
        /** 选择行keys */
        selectedRowKeys?: React.Key[];
        /** 改变 */
        onChange?: (selectedRowKeys: React.Key[], selectedRows: any[]) => void;
    };
    /** 文件选项 */
    filesOptions?: FilesOptions;
    /** 编辑组件选项 */
    editOptions?: {
        /** 编辑属性 */
        editProps?: BaseWebEditUniversalProps;
        /** 编辑组件 */
        Editor?: React.ComponentType<BaseWebEditUniversalProps>;
    }
    /** 显示选项 */
    displayOptions?: DisplayOptionsProps;
    /** 禁用选项 */
    disabledOptions?: DisabledOptionsProps;
    /** 展开列选项 */
    expandableOptions?: ExpandableOptions;
    /** 回调 */
    onCallbacks?: {
        /**
          * 在翻页、查询、视图切换等用户操作触发数据请求后执行
          * @param action - 操作类型及相关数据
          */
        onAfterAction?: (action: {
            /** 类型 */
            type: DataActionType;
            /** 数据 */
            data?: any;
        }) => void;
        /**
          * 覆盖删除功能
          * @param options 选项
          * @returns 
          */
        onDelete?: (options: DeleteOptions) => Promise<RtnType>;
        /**
          * 删除前
          * @param options 选项
          * @returns 
          */
        onBeforeDelete?: (options: DeleteOptions) => Promise<RtnType>;
        /**
          * 删除后
          * @param rtn 删除返回
          * @returns 
          */
        onAfterDelete?: (rtn: RtnType) => void;
    };
    /**
      * 自定义渲染表格上方额外区域，可接收当前数据和刷新方法
      * @param options 选项
      * @returns 
      */
    renderHeadExtra?: (
        options: {
            /** 数据列 */
            rows: Array<any> | undefined;
            /** 获取数据 */
            fetchData: (options?: FetchDataOptions) => Promise<RtnType>;
            /** 修改单条记录的单个字段 */
            updateOne: (options: UpdateOneOptions) => Promise<RtnType>;
        }) => React.ReactNode;
};

/** 刷新数据选项 */
export interface FetchDataOptions extends
    Pick<ShowPageProps, 'sendPageData' | 'current' | 'pageSize' | 'orderByString' | 'queryString' | 'useBak'>,
    Partial<Pick<CommonProps, 'apiId'>> {
};

/** 修改单条记录的单个字段选项 */
export interface UpdateOneOptions {
    /** 主健值 */
    id: any;
    /** 字段 */
    columnName: string;
    /** 设置的变量 */
    value: boolean;
    /** apiId */
    targetApiId?: string;
};

/**
 * 将 boolean | ShowBooleanColumnOptions 归一化为完整的列配置
 * @param value 传入的配置（布尔值或对象）
 * @param defaults 默认配置
 * @returns 归一化后的完整配置
 */
const normalizeBooleanColumnOptions = (
    value: boolean | ShowBooleanColumnOptions | undefined,
    defaults: Required<Pick<ShowBooleanColumnOptions, 'title' | 'columnName' | 'trueValue' | 'falseValue'>> &
        Pick<ShowBooleanColumnOptions, 'checkedChildren' | 'unCheckedChildren'>
): Required<Pick<ShowBooleanColumnOptions, 'title' | 'columnName' | 'trueValue' | 'falseValue'>> &
    Pick<ShowBooleanColumnOptions, 'checkedChildren' | 'unCheckedChildren'> => {
    if (typeof value === 'object' && value !== null) {
        return {
            title: value.title ?? defaults.title,
            columnName: value.columnName ?? defaults.columnName,
            checkedChildren: value.checkedChildren ?? defaults.checkedChildren,
            unCheckedChildren: value.unCheckedChildren ?? defaults.unCheckedChildren,
            trueValue: value.trueValue ?? defaults.trueValue,
            falseValue: value.falseValue ?? defaults.falseValue,
        };
    }
    return defaults;
};

/**
 * 基础通用列表组件
 */
const BaseWebUniversal = forwardRef<any, BaseWebUniversalProps>((props, ref) => {
    const { titleName, apiId, filesOptions, editOptions, defaultService,
        displayOptions: initDisplayOptions, disabledOptions: initDisabledOptions,
        primaryKeyName = 'id', sendPageData: initSendPageData, columns, expandableOptions,
        renderHeadExtra, onCallbacks
    } = props;
    const { filesApiId = `${apiId}File`, filesColumns, FilesEditComponent,
        filesServiceUrl = defaultService, fileParentId, insertFile, defaultSelectedOptions, filesArrayName } = filesOptions || {};
    // ===== 组件内部UI状态（非业务状态仍保留本地） =====
    //显示选项（配置级别，保留本地状态）
    const [displayOptions, setDisplayOptions] = useState<DisplayOptionsProps>({
        autoFetch: initDisplayOptions?.autoFetch ?? true,
        viewMode: initDisplayOptions?.viewMode ?? 'table',
        galleryColumns: initDisplayOptions?.galleryColumns ?? undefined,
        pageSize: initDisplayOptions?.pageSize ?? 10,
        gallerPageSize: initDisplayOptions?.gallerPageSize ?? 15,
        showAdd: initDisplayOptions?.showAdd ?? true,
        showQuery: initDisplayOptions?.showQuery ?? true,
        showImage: initDisplayOptions?.showImage ?? false,
        showDownload: initDisplayOptions?.showDownload ?? false,
        showDefault: initDisplayOptions?.showDefault ?? false,
        showStates: initDisplayOptions?.showStates ?? true,
        showOperation: initDisplayOptions?.showOperation ?? true,
        showDelete: initDisplayOptions?.showDelete ?? true,
        deleteButton: initDisplayOptions?.deleteButton ?? {},
        showEdit: initDisplayOptions?.showEdit ?? true,
        showFiles: initDisplayOptions?.showFiles ?? false,
        showUseBak: initDisplayOptions?.showUseBak ?? false,
        showRowSelection: initDisplayOptions?.showRowSelection ?? false,
        loggerProps: initDisplayOptions?.loggerProps ?? {},
    });
    /** Redux，page库数据 */
    const pageStore: PageStoreProps = useSelector((state: any) => state.pageStore);
    /** Redux发送函数 */
    const dispatch = useDispatch();

    // ===== 从 Redux 读取所有业务状态（去掉本地 useState） =====
    const {
        current = 1,
        rows,
        queryString = undefined,
        orderByString = undefined,
        total = 0,
        pageSize = displayOptions.viewMode === 'gallery' ?
            (displayOptions.gallerPageSize ? displayOptions.gallerPageSize : 12) :
            (displayOptions.pageSize ? displayOptions.pageSize : 10),
        useBak = false,
        // sendPageData = { ...initSendPageData },
    } = pageStore.cache[apiId]?.data || {};

    // 优先使用外部传入的 initSendPageData，如果没有（undefined）则用缓存中的旧值
    const cachedSendPageData = pageStore.cache[apiId]?.data?.sendPageData;
    // 用 useMemo 固定 sendPageData 引用，避免每次 render 产生新对象导致下游 useCallback 重建
    const sendPageData = useMemo(
        () => (initSendPageData !== undefined ? initSendPageData : cachedSendPageData),
        [initSendPageData, cachedSendPageData]
    );

    // ===== 用 ref 保存最新状态，供 onFetchData 内部读取，避免闭包过期 =====
    const latestStateRef = useRef({
        current,
        pageSize,
        queryString,
        orderByString,
        useBak,
        sendPageData,
    });
    // 每次 render 同步最新值（不放进 useCallback 依赖，避免 onFetchData 被频繁重建）
    latestStateRef.current = {
        current,
        pageSize,
        queryString,
        orderByString,
        useBak,
        sendPageData,
    };

    //table数据多行选择
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const mergedRowSelection = useMemo(() => ({
        ...props.rowSelectionConfig,
        selectedRowKeys: props.rowSelectionConfig?.selectedRowKeys ?? selectedRowKeys,
        onChange: (keys: React.Key[], rows: any[]) => {
            props.rowSelectionConfig?.onChange?.(keys, rows);
            if (!props.rowSelectionConfig?.selectedRowKeys) {
                setSelectedRowKeys(keys);
                setSelectedRows(rows);
            }
        },
    }), [props.rowSelectionConfig, selectedRowKeys, selectedRows]);

    useEffect(() => {
        if (pageStore.cache[apiId]?.success === false) {
            antdMessage('error', pageStore.cache[apiId]?.message ?? '无数据');
        }
    }, [pageStore.cache[apiId]?.success]);

    // 当前视图模式：根据 displayOptions 初始化（UI控制）
    const [currentViewMode, setCurrentViewMode] = useState<ViewModeStringType>(
        displayOptions?.viewMode ? (displayOptions?.viewMode !== 'both' ? displayOptions?.viewMode : 'table') : 'table');

    const [modalAEVisible, setModalAEVisible] = useState<boolean>(false);       // 编辑/新增弹窗

    const [modalLoggerVisible, setModalLoggerVisible] = useState<boolean>(false);
    const [loggerData, setLoggerData] = useState<BaseWebUniversalLoggerProps>({
        closeModal: () => setModalLoggerVisible(false)
    });
    const [subColumnsData, setSubColumnsData] = useState<any>(expandableOptions?.subColumns); // 子表格列定义
    const [expandedData, setExpandedData] = useState<any>();       // 子表格数据缓存

    const [modalFilesExtendsManageVisible, setModalFilesExtendsManageVisible] = useState<boolean>(false);
    const [filesExtendsManageData, setFilesExtendsManageData] = useState<FilesExtendsManageProps>({
        closeModal: () => setModalFilesExtendsManageVisible(false),
        editOptions: {
            Editor: FilesEditComponent
        },
        apiId: filesApiId,
        defaultService: filesServiceUrl,
        titleName: '文件管理',
        sendPageData,
        columns: filesColumns!,
        insertFile,
        defaultSelectedOptions
    });

    //禁用选项（保留本地）
    const [disabledOptions, setDisabledOptions] = useState<DisabledOptionsProps>({
        disabledAdd: initDisabledOptions?.disabledAdd ?? false,
    });

    //删除按纽显示的popover（UI状态，保留本地）
    const [deleteRowState, setDeleteRowState] = useState<
        Record<string, { visible: boolean; checked: boolean }>
    >({});

    // 查询输入框受控值（临时本地状态）
    const [queryInput, setQueryInput] = useState(queryString ?? '');

    // 同步 queryInput 当 Redux 中的 queryString 变化时（例如重置或外部修改）
    useEffect(() => {
        setQueryInput(queryString ?? '');
    }, [queryString]);

    /**
     * 更新显示属性，只更新非undefined的字段
     * @param options 选项
     */
    const updateDisplayOptions = (options: Partial<DisplayOptionsProps>) => {
        setDisplayOptions(prev => ({
            ...prev,
            ...Object.fromEntries(
                Object.entries(options).filter(([_, v]) => v !== undefined)
            ),
        }));
    };

    // ===== 布尔开关列的归一化配置 =====
    /**
     * 状态列配置
     * 默认值：标题 '状态'、字段 'isUse'、开关文案 '启用/关闭'、真假值 true/false
     */
    const statesColumnOptions = normalizeBooleanColumnOptions(displayOptions.showStates, {
        title: '状态',
        columnName: 'isUse',
        checkedChildren: '启用',
        unCheckedChildren: '关闭',
        trueValue: true,
        falseValue: false,
    });

    /**
     * 默认列配置
     * 默认值：标题 '默认显示'、字段 'isDefault'、无开关文案、真假值 true/false
     */
    const defaultColumnOptions = normalizeBooleanColumnOptions(displayOptions.showDefault, {
        title: '默认显示',
        columnName: 'isDefault',
        checkedChildren: undefined,
        unCheckedChildren: undefined,
        trueValue: true,
        falseValue: false,
    });

    // ---------- 回调函数 ----------
    /**
     * 在翻页、查询、视图切换等用户操作触发数据请求后执行
     * @param type 类型
     * @param data 数据
     */
    const triggerAfterAction = (type: DataActionType, data?: any) => {
        onCallbacks?.onAfterAction?.({ type, data });
    };

    /**
     * 获取数据（现在只负责 dispatch 到 Redux，由 saga 处理请求并更新状态）
     * 说明：内部读取 latestStateRef.current，避免闭包捕获过期的 queryString / orderByString 等状态，
     *      从而在编辑弹窗保存后触发的刷新不会把原有查询条件冲掉。
     * @param options 选项
     */
    const onFetchData = useCallback(async (options?: FetchDataOptions): Promise<RtnType> => {
        const rtn = defaultRtn();
        try {
            // 从 ref 中读取最新状态，options 中显式传入的优先
            const latest = latestStateRef.current;
            const newCurrent = options?.current ?? latest.current;
            const newPageSize = options?.pageSize ?? latest.pageSize;
            const newQueryString = options?.queryString ?? latest.queryString;
            const newOrderByString = options?.orderByString ?? latest.orderByString;
            const newUseBak = options?.useBak ?? latest.useBak;
            const newSendPageData = options?.sendPageData ?? latest.sendPageData;

            dispatch(submitData({
                apiId,
                cache: {
                    [apiId]: {
                        fetchUrl: defaultService,
                        data: {
                            current: newCurrent,
                            pageSize: newPageSize,
                            sendPageData: newSendPageData,
                            queryString: newQueryString,
                            orderByString: newOrderByString,
                            useBak: newUseBak
                        }
                    }
                }
            }));
            rtn.success = true;
        }
        catch (error: any) {
            rtn.success = false;
            rtn.message = '获取数据错误！';
            if (error) {
                rtn.message = `获取数据错误！${error.message || error}`;
            }
        }
        return rtn;
        // 依赖里只保留稳定项，避免因状态变化导致 onFetchData 频繁重建，
        // 同时也避免 useImperativeHandle 每次都生成新的 ref.current 指向
    }, [apiId, defaultService, dispatch]);

    /** 分页配置（数据全部来自 Redux） */
    const tablePagination = {
        showSizeChanger: true,
        showQuickJumper: false,
        showTotal: () => `共${total}条`,
        pageSize,
        current,
        total,
        // 移除 onChange，分页变化统一由 Table 的 onChange 处理
    };

    useEffect(() => {
        if (displayOptions.autoFetch === false) return;
        onFetchData();
    }, [displayOptions]);


    // ===== 事件处理函数 =====
    /** 修改单条记录的单个字段 */
    const handleUpdateOne = async (options: UpdateOneOptions): Promise<RtnType> => {
        let rtn = defaultRtn();
        const { id, columnName, value, targetApiId } = options;
        const body = {
            apiId: targetApiId ? targetApiId : apiId,
            apiVariable: id,
            apiExtend: 'updateOne',
            apiData: { columnName, value },
        };
        rtn = await fetchApi.PUT(defaultService, { body });
        if (rtn.success === true) {
            antdMessage(
                'success',
                'updateOne修改成功',
                { description: `apiId：${body.apiId}，columnName：${body.apiData.columnName}，value：${body.apiData.value}` }
            );
            //刷新新数据
            onFetchData();
        } else {
            antdMessage('error', '修改失败', { description: rtn.message });
        }
        return rtn;
    };

    /** 删除记录 */
    const handleDelete = async (record: any, isChecked: boolean): Promise<RtnType<any>> => {
        let rtn = defaultRtn({ success: true });
        try {
            if (onCallbacks?.onDelete) {
                rtn = await onCallbacks?.onDelete({
                    record,
                    sendPageData
                });
            } else {
                rtn = await onCallbacks?.onBeforeDelete?.({ record, sendPageData }) || defaultRtn({ success: true });
                if (rtn.success === true) {
                    rtn = await fetchApi.DELETE(defaultService, {
                        body: {
                            apiId,
                            apiVariable: record[primaryKeyName],
                            apiData: { sendPageData }
                        }
                    });
                }
                if (isChecked && onCallbacks?.onAfterDelete) {
                    onCallbacks?.onAfterDelete?.(rtn);
                }
                if (rtn.success) {
                    antdMessage('success', '删除成功', { description: 'success' });
                    onFetchData();
                } else {
                    antdMessage('error', '删除失败', { description: rtn.message });
                }
            }
        }
        catch (error: any) {
            rtn.success = false;
            rtn.message = '删除失败';
            if (error) {
                rtn.message = `删除失败${error.message || error}`;
            }
        }
        return rtn;
    };

    /**
     * 查看日志
     * @param queryId 日志queryId
     */
    const handleShowLogger = (queryId?: string) => {
        setLoggerData({
            ...displayOptions.loggerProps,
            ...loggerData,
            loggerServiceUrl: displayOptions.loggerProps?.loggerServiceUrl ?? defaultService,
            queryType: displayOptions.loggerProps?.queryType ?? apiId,
            queryId,
            isLoggerTypeSelect: false,
            isLoggerClear: false
        })
        setModalLoggerVisible(true);
    };

    /** 关闭新增/编辑的回调 */
    const onCloseModalAE = (state?: RtnType<any>) => {
        setModalAEVisible(false);
        if (state?.success) {
            // 编辑成功后刷新数据
            onFetchData();
        }
    };

    const { editProps: initEditProps, Editor } = editOptions || {};
    const [editProps, setEditProps] = useState<BaseWebEditUniversalProps>({
        apiId: initEditProps?.apiId ?? apiId,
        defaultService: initEditProps?.defaultService ?? defaultService,
        titleName: initEditProps?.titleName ?? titleName,
        primaryKeyName: initEditProps?.primaryKeyName ?? primaryKeyName,
        closeModal: initEditProps?.closeModal ?? onCloseModalAE,
        sendPageData: {
            ...initEditProps?.sendPageData,
            ...sendPageData
        },
        editingId: initEditProps?.editingId,
        formOptions: initEditProps?.formOptions,
        dateOptions: initEditProps?.dateOptions,
        displayOptions: initEditProps?.displayOptions,
        disabledOptions: initEditProps?.disabledOptions,
        onCallbacks: initEditProps?.onCallbacks
    });

    /**
     * 打开新增/编辑弹窗
     * @param editMode 编辑模式
     * @param id 编辑模式下获取数据的id值
     */
    const openModalAE = (editMode: EditMode, id?: string) => {
        setEditProps(prev => ({
            ...prev,
            editMode,
            editingId: id,
            sendPageData: {
                ...prev.sendPageData,
                ...sendPageData
            }
        }));
        setModalAEVisible(true);
    };

    // ==================== 暴露给父组件的方法 ====================
    useImperativeHandle(ref, () => ({
        /**
         * 获取数据
         * @param options 选项
         * @returns 
         */
        fetchData: (options?: FetchDataOptions) => onFetchData(options),
        /**
         * 修改单条记录的单个字段
         * @param options 选项
         * @returns 
         */
        updateOne: (options: UpdateOneOptions) => handleUpdateOne(options),
        /**
         * 动态设置子表格列定义
         * @param subColumns 子表格
         */
        setSubColumns: (subColumns: any) => {
            setSubColumnsData(subColumns);
        },
        /**
         * 获取或操作当前表格的选中行状态
         * @param action - 操作类型：
         *   - 'getKeys'  返回选中的行 key 数组
         *   - 'getRows'  返回选中的行数据数组
         *   - 'clear'    清空所有选中行
         *   - 不传则返回包含 { selectedRowKeys, selectedRows } 的对象
         * @returns 根据 action 返回对应数据，或 undefined（clear 时无返回值）
         */
        selection: (action?: 'getKeys' | 'getRows' | 'clear') => {
            if (action === 'getKeys') return selectedRowKeys;
            if (action === 'getRows') return selectedRows;
            if (action === 'clear') {
                setSelectedRowKeys([]);
                setSelectedRows([]);
            }
            // 不传参数返回完整信息
            return { selectedRowKeys, selectedRows };
        },
        /** 传递的页面数据 */
        sendPageData,
        /** 服务基础地址 */
        defaultService,
    }), [sendPageData, onFetchData, selectedRows, selectedRowKeys]);

    /**
     * 获取文件
     * @param record 
     * @returns 
     */
    const getFile = (record: any) => {
        /** 返回值 */
        let res = {
            /** 文件地址 */
            fileUrl: '',
            /** 是否图片 */
            isImage: false
        }
        if (typeof record === 'undefined') {
            return res;
        }
        /** 文件类型 */
        let fileType;
        try {
            if (typeof filesArrayName !== 'undefined') {
                if (typeof record[filesArrayName!] !== 'undefined') {
                    fileType = record[filesArrayName!][0].fileType;
                    res.fileUrl = `${record.filesServerUrl}/${record[filesArrayName!][0].fileDestination}`;
                }
            }
            else {
                if (typeof record[apiId + "Files"] !== "undefined") {
                    fileType = record[apiId + "Files"][0].fileType;
                    res.fileUrl = `${record.filesServerUrl}/${record[apiId + "Files"][0].fileDestination}`;
                }
                else {
                    fileType = record.fileType;
                    res.fileUrl = `${record.filesServerUrl}/${record.fileDestination}`;
                }
            }
            if (typeof fileType !== 'undefined') {
                if (fileType.slice(0, 5) === 'image') {
                    res.isImage = true
                }
            }
        }
        catch { }
        return res;
    };

    /** 操作表头 */
    let operationColumns = [
        (displayOptions.showImage ? {
            title: '图片',
            dataIndex: 'id',
            render: (_val: string, record: any) => (
                (getFile(record)).isImage ?
                    < Image
                        src={(getFile(record)).fileUrl}
                        width={60}
                        alt="图片" /> :
                    <text>{record.fileType}</text>
            )
        } : {}),
        (displayOptions.showFiles ? {
            title: '文件',
            dataIndex: 'operation',
            key: 'operation',
            render: (_val: any, record: any) =>
                <Space size="small">
                    <Button size={"small"} type="primary"
                        onClick={() => {
                            setFilesExtendsManageData(prev => ({
                                ...prev,
                                sendPageData: {
                                    ...prev.sendPageData,
                                    ...sendPageData,
                                    [fileParentId ? fileParentId : `${apiId}${stringHandle.firstUpperCase(primaryKeyName)}`]: record.id
                                }
                            }));
                            setModalFilesExtendsManageVisible(true);
                        }}>文件</Button>
                </Space>
        } : {}),
        (displayOptions.showDownload ? {
            title: '下载',
            dataIndex: 'fileName',
            render: (_val: string, record: any) =>
                <a href={(getFile(record)).fileUrl} >
                    <Tooltip title={'请用鼠标右键点击下载（另存）！'}>下载</Tooltip></a>
        } : {}),
        // 默认显示列：使用归一化配置（标题、字段名、开关文案、真假值均可自定义）
        (displayOptions.showDefault ? {
            title: defaultColumnOptions.title,
            dataIndex: defaultColumnOptions.columnName,
            render: (val: any, record: any) => (
                <Switch
                    checkedChildren={defaultColumnOptions.checkedChildren}
                    unCheckedChildren={defaultColumnOptions.unCheckedChildren}
                    checked={val === defaultColumnOptions.trueValue || val === true}
                    key={record[primaryKeyName]}
                    onChange={(checked) =>
                        handleUpdateOne?.({
                            id: record[primaryKeyName],
                            columnName: defaultColumnOptions.columnName,
                            value: checked ? (defaultColumnOptions.trueValue as boolean) : (defaultColumnOptions.falseValue as boolean)
                        })
                    }
                />
            )
        } : {}),
        // 状态列：使用归一化配置（标题、字段名、开关文案、真假值均可自定义）
        (displayOptions.showStates ? {
            title: statesColumnOptions.title,
            dataIndex: statesColumnOptions.columnName,
            render: (val: any, record: any) => (
                <Switch
                    checkedChildren={statesColumnOptions.checkedChildren}
                    unCheckedChildren={statesColumnOptions.unCheckedChildren}
                    checked={val === statesColumnOptions.trueValue || val === true}
                    key={record[primaryKeyName]}
                    onChange={(checked) =>
                        handleUpdateOne?.({
                            id: record[primaryKeyName],
                            columnName: statesColumnOptions.columnName,
                            value: checked ? (statesColumnOptions.trueValue as boolean) : (statesColumnOptions.falseValue as boolean)
                        })
                    }
                />
            )
        } : {}),
        (displayOptions.showOperation ? {
            title: '操作',
            dataIndex: 'operation',
            key: 'operation',
            render: (text: any, record: any) =>
                <Space size="small">
                    {displayOptions.showDelete ? (
                        <>
                            <Popover
                                content={
                                    <>
                                        {displayOptions?.deleteButton?.useCheckBox ? (
                                            <Checkbox
                                                checked={
                                                    deleteRowState[record[primaryKeyName]]?.checked ??
                                                    displayOptions?.deleteButton?.isChecked ??
                                                    false
                                                }
                                                onClick={() => {
                                                    setDeleteRowState((prev) => {
                                                        const current = prev[record[primaryKeyName]];
                                                        const nextChecked = !(
                                                            current?.checked ??
                                                            displayOptions?.deleteButton?.isChecked ??
                                                            false
                                                        );
                                                        return {
                                                            ...prev,
                                                            [record[primaryKeyName]]: {
                                                                ...current,
                                                                checked: nextChecked,
                                                            },
                                                        };
                                                    });
                                                }}
                                            >
                                                {displayOptions?.deleteButton?.checkBoxContent
                                                    ? displayOptions?.deleteButton.checkBoxContent
                                                    : '是否删除'}
                                            </Checkbox>
                                        ) : null}
                                        <p />
                                        <Space>
                                            <Button
                                                onClick={() => {
                                                    setDeleteRowState((prev) => ({
                                                        ...prev,
                                                        [record[primaryKeyName]]: {
                                                            ...prev[record[primaryKeyName]],
                                                            visible: false,
                                                        },
                                                    }));
                                                }}
                                            >
                                                否
                                            </Button>
                                            <Button
                                                onClick={() => {
                                                    if (typeof handleDelete !== 'undefined') {
                                                        handleDelete(
                                                            record,
                                                            deleteRowState[record[primaryKeyName]]?.checked ??
                                                            displayOptions?.deleteButton?.isChecked ??
                                                            false
                                                        );
                                                        // 删除后清除该行的临时状态
                                                        setDeleteRowState((prev) => {
                                                            const { [record[primaryKeyName]]: _, ...rest } = prev;
                                                            return rest;
                                                        });
                                                    }
                                                }}
                                            >
                                                是
                                            </Button>
                                        </Space>
                                    </>
                                }
                                title={`是否删除 ${record[primaryKeyName]}？`}
                                trigger="click"
                                open={deleteRowState[record[primaryKeyName]]?.visible ?? false}
                                onOpenChange={(visible) => {
                                    setDeleteRowState((prev) => ({
                                        ...prev,
                                        [record[primaryKeyName]]: {
                                            ...prev[record[primaryKeyName]],
                                            visible,
                                        },
                                    }));
                                }}
                            >
                                <Button size="small" type="text" danger>
                                    删除
                                </Button>
                            </Popover>
                        </>
                    ) : null}
                    {displayOptions.showEdit ?
                        <Button size={"small"} type="text" onClick={() =>
                            openModalAE('EDIT', record[primaryKeyName])
                        }>编辑</Button> : ''}
                </Space>
        } : {}),
        (displayOptions.loggerProps?.showSingleLogger ? {
            title: '日志',
            dataIndex: 'logger',
            key: 'logger',
            render: (text: any, record: any) =>
                <Button size={"small"} type="text" onClick={() => {
                    if (handleShowLogger) {
                        handleShowLogger(record[primaryKeyName]);
                    }
                }
                }>日志</Button>
        } : {})
    ];

    //合拼表头(先过滤空对象，再与外部列合并，避免重复逻辑)
    operationColumns = operationColumns.filter(valeus => Object.keys(valeus).length !== 0);
    let currentColumns;
    if (columns) {
        currentColumns = columns.concat(operationColumns);
    } else {
        currentColumns = operationColumns;
    }

    /**
     * 子表格渲染函数
     * @param item 内容
     * @returns 
     */
    const getExpandedRowRender = (item: any) => {
        if (!expandedData) return undefined;
        const { isSameMainDisplayOptions, isTree, parentKey, subDataName, childrenKey = 'children' } = expandableOptions || {};
        const rowData = expandedData[item[primaryKeyName]];
        let dataSource = Array.isArray(rowData) ? rowData : rowData ? [rowData] : undefined;
        /** 当前子表格 */
        let currentSubColumns = subColumnsData;
        if (isSameMainDisplayOptions) {
            currentSubColumns = currentColumns;
        }

        /**
         * 将扁平数据转换为树形数据
         * @param list 扁平数组
         * @param idName 主键字段名
         * @param parentIdName 父级字段名
         */
        const toTreeData = (list: any[], idName: string, parentKey: string = 'parentId') => {
            const map = new Map();
            const roots: any[] = [];
            // 初始化 map
            list.forEach(item => map.set(item[idName], { ...item, children: [] }));
            // 挂载节点
            list.forEach(item => {
                const node = map.get(item[idName]);
                const parentId = item[parentKey];
                if (parentId && map.has(parentId)) {
                    map.get(parentId).children.push(node);
                } else {
                    roots.push(node);
                }
            });
            return roots;
        };
        // 树形子表格
        if (isTree) {
            // 如果数据是扁平的且配置了 parentIdName，则先转换
            if (parentKey && Array.isArray(dataSource)) {
                dataSource = toTreeData(dataSource, primaryKeyName, parentKey);
            }
            return (
                <Table
                    columns={currentSubColumns}
                    rowKey={(row: any) => row[primaryKeyName] || row.id}
                    pagination={false}
                    dataSource={dataSource}
                    size="small"
                    expandable={{
                        childrenColumnName: childrenKey,
                        defaultExpandAllRows: true
                    }}
                />
            );
        }

        return (
            <Table
                columns={currentSubColumns}
                rowKey={(row: any) => row[primaryKeyName] || row.id}
                pagination={false}
                dataSource={dataSource}
            />
        );
    };

    /**
     * 展开子表格
     */
    const onExpandClick = useCallback(
        (status: boolean, item: any) => {
            if (!status) return;
            let expandableData = expandableOptions?.subDataName ? item[expandableOptions?.subDataName] : item;
            if (expandableOptions?.isIncludingMasterData) {
                if (Array.isArray(expandableData)) {
                    expandableData = expandableData.map((row) => ({ ...row, $masterData: item }));
                } else {
                    expandableData = { ...expandableData, $masterData: item };
                }
            }
            setExpandedData((prev: any) => ({
                ...prev,
                [item[primaryKeyName]]: expandableData,
            }));
        },
        [expandableOptions?.subDataName, expandableOptions?.isIncludingMasterData, primaryKeyName]
    );

    // ===== 表格排序/分页/筛选统一处理（新增，整合分页与排序逻辑，参照 WebUniversal 的 handleSort） =====
    /**
     * 表格的 onChange 回调，统一处理分页、排序、筛选等变化，生成 orderByString 并请求数据
     * @param pagination 当前分页信息
     * @param _filters 筛选信息
     * @param sorter 当前排序状态
     * @param extra 额外信息，包含 action 类型（'paginate' | 'sort' | 'filter'）
     */
    const handleTableChange = useCallback((pagination: any, _filters: any, sorter: any, extra: any) => {
        // 处理排序逻辑：仅处理单列排序（多列排序时 sorter 为数组，此处暂不处理）
        let newOrderByString = '';
        if (!Array.isArray(sorter) && sorter.field && sorter.order) {
            newOrderByString = `${sorter.field}@${sorter.order === 'descend' ? 'DESC' : 'ASC'}`;
        }

        // 判断操作类型：排序/筛选时回到第一页，分页时使用 pagination 中的页码
        const isSortOrFilter = extra.action === 'sort' || extra.action === 'filter';
        const newCurrent = isSortOrFilter ? 1 : pagination.current;
        const newPageSize = pagination.pageSize;

        // 提交新的参数并请求数据
        dispatch(submitData({
            apiId,
            cache: {
                [apiId]: {
                    fetchUrl: defaultService,
                    data: {
                        current: newCurrent,
                        pageSize: newPageSize,
                        queryString,
                        orderByString: newOrderByString,
                        useBak,
                        sendPageData
                    }
                }
            }
        }));

        // 触发回调，通知外部操作类型与相关数据
        triggerAfterAction(
            extra.action === 'sort' ? 'sort' : extra.action === 'filter' ? 'filter' : 'paginate',
            { current: newCurrent, pageSize: newPageSize, orderByString: newOrderByString }
        );
    }, [apiId, defaultService, dispatch, queryString, useBak, sendPageData, triggerAfterAction]);

    // ===== 图库模式下渲染单个卡片的内容 =====
    const renderGalleryItem = (record: any) => {
        const cols = displayOptions.galleryColumns || columns; // 如果未传 galleryColumns，回退到表格列
        return (
            <Card size="small" hoverable style={{ marginBottom: 16 }}>
                {cols.map((col: any) => {
                    const value = typeof col.render === 'function' ? col.render(record[col.dataIndex], record) : record[col.dataIndex];
                    return (
                        <div key={col.dataIndex || col.key} style={{ marginBottom: 4 }}>
                            {col.title ? <span style={{ fontWeight: 'bold', marginRight: 4 }}>{col.title}：</span> : null}
                            {value}
                        </div>
                    );
                })}
            </Card>
        );
    };

    // ===== 图库视图 =====
    const renderGalleryView = () => {
        // 统一处理分页变化（页码或每页条数变化）
        const handlePaginationChange = (page: number, pageSize: number) => {
            dispatch(submitData({
                apiId,
                cache: {
                    [apiId]: {
                        fetchUrl: defaultService,
                        data: {
                            current: page,
                            pageSize,
                            queryString,
                            orderByString,
                            useBak,
                            sendPageData
                        }
                    }
                }
            }));
            triggerAfterAction('paginate', { current: page, pageSize });
        };

        // 构造分页配置，补充 onChange 和 onShowSizeChange
        const paginationProps = {
            ...tablePagination,
            onChange: handlePaginationChange,
            onShowSizeChange: (current: number, size: number) => {
                // 当每页条数变化时，通常重置到第一页，但保留新 size
                handlePaginationChange(1, size);
            }
        };

        return (
            <List
                style={{ marginTop: 8 }}
                grid={{ gutter: 16, column: 5 }}
                dataSource={rows}
                pagination={paginationProps}
                renderItem={(item: any) => (
                    <List.Item key={item[primaryKeyName]}>
                        {renderGalleryItem(item)}
                    </List.Item>
                )}
            />
        );
    };

    return (
        <>
            <Space>
                {typeof renderHeadExtra === 'function' ?
                    renderHeadExtra({ rows, fetchData: onFetchData, updateOne: handleUpdateOne }) :
                    null}
                {/* 新增按钮 */}
                {(displayOptions.showAdd === true) && (
                    <Button disabled={disabledOptions.disabledAdd} onClick={() => openModalAE('NEW', '')}>
                        新增{titleName}
                    </Button>
                )}
                {/* 视图切换选择器 */}
                {displayOptions.viewMode === 'both' && (
                    <Select value={currentViewMode} onChange={(e) => {
                        let currentPageSize = displayOptions?.pageSize;
                        if (e === 'gallery') {
                            currentPageSize = displayOptions?.gallerPageSize;
                        }
                        // 直接 dispatch 更新 pageSize，并触发数据请求
                        dispatch(submitData({
                            apiId,
                            cache: {
                                [apiId]: {
                                    fetchUrl: defaultService,
                                    data: {
                                        current,
                                        pageSize: currentPageSize,
                                        queryString,
                                        orderByString,
                                        useBak,
                                        sendPageData
                                    }
                                }
                            }
                        }));
                        setCurrentViewMode(e);
                        triggerAfterAction('viewModeChange', { viewMode: e });
                    }}>
                        {getList.general(ViewModes.getAll({ filter: { isSelect: true } }))}
                    </Select>
                )}
                {/* 日志按钮 */}
                {(displayOptions.loggerProps?.showLogger === true) && (
                    <Button onClick={() => handleShowLogger()}>
                        日志
                    </Button>
                )}
                {/* 查询区域 */}
                {(displayOptions.showQuery === true) && (
                    <>
                        <Tooltip placement="topLeft" title={'未定'}>
                            <Input
                                placeholder="请输入查询内容"
                                value={queryInput}
                                onChange={(e) => setQueryInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        dispatch(submitData({
                                            apiId,
                                            cache: {
                                                [apiId]: {
                                                    fetchUrl: defaultService,
                                                    data: {
                                                        current,
                                                        pageSize,
                                                        queryString: queryInput,
                                                        orderByString,
                                                        useBak,
                                                        sendPageData
                                                    }
                                                }
                                            }
                                        }));
                                        triggerAfterAction('query', { queryString: queryInput });
                                    }
                                }}
                            />
                        </Tooltip>
                        <Button
                            icon={<SearchOutlined />}
                            onClick={() => {
                                dispatch(submitData({
                                    apiId,
                                    cache: {
                                        [apiId]: {
                                            fetchUrl: defaultService,
                                            data: {
                                                current,
                                                pageSize,
                                                queryString: queryInput,
                                                orderByString,
                                                useBak,
                                                sendPageData
                                            }
                                        }
                                    }
                                }));
                                triggerAfterAction('query', { queryString: queryInput });
                            }}
                        >
                            查询
                        </Button>
                    </>
                )}
                {/* 使用备份数据勾选框 */}
                {displayOptions.showUseBak && (
                    <Tooltip placement="topLeft" title="获取数据时同时使用备份数据">
                        <Checkbox checked={useBak} onChange={(e) => {
                            const isUseBak = e.target.checked;
                            dispatch(submitData({
                                apiId,
                                cache: {
                                    [apiId]: {
                                        fetchUrl: defaultService,
                                        data: {
                                            current,
                                            pageSize,
                                            queryString,
                                            orderByString,
                                            useBak: isUseBak,
                                            sendPageData
                                        }
                                    }
                                }
                            }));
                            triggerAfterAction('useBakChange', { useBak: isUseBak });
                        }}>
                            备份数据
                        </Checkbox>
                    </Tooltip>
                )}
            </Space>

            {/* ---------- 主内容区：表格或图库 ---------- */}
            {currentViewMode === 'table' ? (
                <Table
                    style={{ marginTop: 8 }}
                    id="table"
                    rowKey={(row: any) => row[primaryKeyName]}
                    columns={currentColumns}
                    dataSource={rows}
                    pagination={tablePagination}
                    size="small"
                    rowSelection={initDisplayOptions?.showRowSelection ? mergedRowSelection : undefined}
                    expandable={
                        expandableOptions
                            ? expandableOptions.useSystemTree
                                ? {
                                      // ===== 系统树形模式 =====
                                      // 直接使用数据中的 children 字段，由 antd 自动构建树形结构。
                                      // 此时不传 expandedRowRender，避免 "expandedRowRender should not use with nested Table" 警告。
                                      childrenColumnName: expandableOptions.childrenKey ?? 'children',
                                  }
                                : {
                                      // ===== 自定义 expandedRowRender 模式 =====
                                      // 关键：把 childrenColumnName 改为一个数据中不存在的字段名，
                                      // 这样 antd 就不会再把数据里的 children 识别为树形结构，
                                      // 从而专心走 expandedRowRender 自定义渲染路径。
                                      childrenColumnName: '__disabled_children__',
                                      expandedRowRender: getExpandedRowRender,
                                      onExpand: onExpandClick,
                                  }
                            : undefined
                    }
                    // 表格排序、筛选等变化事件（统一入口，已整合分页逻辑）
                    onChange={handleTableChange}
                />
            ) : (
                renderGalleryView()
            )}
            {/* ---------- 模态框 ---------- */}
            {/* 编辑/新增弹窗 */}
            {modalAEVisible && (Editor
                ? <Editor {...editProps} /> :
                <BaseWebEditUniversal {...editProps}></BaseWebEditUniversal>)}
            {/* 日志窗口 */}
            {modalLoggerVisible && <BaseWebUniversalLogger  {...loggerData} />}
            {/* 文件扩展管理窗口 */}
            {modalFilesExtendsManageVisible && <FilesExtendsManage  {...filesExtendsManageData} />}

        </>)
});

export default BaseWebUniversal;